// Design tokens are stored as bare HSL channels ("222 92% 4.9%"), matching
// `hsl(var(--token))` usage in Tailwind config — see CLAUDE.md "Color format".
export type HslTriple = [hue: number, saturation: number, lightness: number];

const HSL_TOKEN_PATTERN = /^(-?[\d.]+)\s+(-?[\d.]+)%\s+(-?[\d.]+)%$/;

// Parses a raw `--token` value (e.g. from `getComputedStyle(...).getPropertyValue`)
// into its H/S/L numbers. Returns null for an unset or malformed token so callers
// can fall back to a static color instead of rendering `hsl(NaN NaN% NaN%)`.
export function parseHslToken(raw: string): HslTriple | null {
  const match = raw.trim().match(HSL_TOKEN_PATTERN);
  if (!match) return null;
  return [parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3])];
}

// Linearly interpolates between two HSL tokens by `t` (0 = from, 1 = to),
// clamped so an out-of-range progress value can't extrapolate past either
// endpoint. Used to fade a color continuously (e.g. by scroll position)
// instead of snapping between two fixed classes.
export function interpolateHslTokens(
  from: HslTriple,
  to: HslTriple,
  t: number
): string {
  const clamped = Math.min(1, Math.max(0, t));
  const [h1, s1, l1] = from;
  const [h2, s2, l2] = to;
  // Short-circuit the exact endpoints — `a + (b - a) * t` can land a hair
  // off `b` at t=1 due to float rounding (e.g. 4.9 -> 4.900000000000006).
  const lerp = (a: number, b: number) =>
    clamped === 0 ? a : clamped === 1 ? b : a + (b - a) * clamped;
  // Hue is undefined at 0% saturation (any hue renders the same gray/white/
  // black), so lerping it against a real hue on the other end sweeps
  // through unrelated, visibly saturated colors along the way — e.g. white
  // (hue irrelevant) to navy (hue ~222) would pass through green and teal
  // mid-fade. Snap to whichever endpoint's hue is meaningful instead of
  // lerping it, so only saturation/lightness ramp while hue stays fixed.
  const hue = s1 === 0 ? h2 : s2 === 0 ? h1 : lerp(h1, h2);
  return `hsl(${hue} ${lerp(s1, s2)}% ${lerp(l1, l2)}%)`;
}
